import { useEffect, useRef } from 'react'
import { createFileRoute, redirect, Outlet } from '@tanstack/react-router'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { usePOSStore } from '@/stores/posStore'
import { useBackfillSelectedStore } from '@/api/stores'
import { useGetCashInStatus } from '@/api/auth'
import { useInactivityLogout } from '@/hooks'
import { CashInModal, CashOutModal, ClerkPinReauthModal } from '@/components/modals'
import { QuickActionsSheet } from '@/components/functions/QuickActionsSheet'

export const Route = createFileRoute('/pos')({
  beforeLoad: () => {
    const { isAuthenticated, selectedRegister, sessionToken } = useSessionStore.getState()

    if (!sessionToken) {
      throw redirect({ to: '/auth' })
    }

    if (!selectedRegister) {
      throw redirect({ to: '/select-register' })
    }

    if (!isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: POSLayout,
})

function POSLayout() {
  useBackfillSelectedStore()
  useInactivityLogout()

  // Reload-during-reauth resume. A hard refresh while the clerk re-PIN
  // overlay is up rehydrates as authenticated-but-clerkless (clerkToken
  // null is persisted; the overlay flag is in-memory) — the route
  // guards pass, but every register-scoped call would 403. Rather than
  // waiting for the first 403 round-trip to re-raise the overlay
  // (client.ts recoverDeadClerkSession also covers that), raise it
  // deterministically on mount/whenever the state appears. Gated on
  // isAuthenticated so clerk logout flows (which flip it false before
  // navigating) can't re-raise mid-transition.
  const clerkToken = useSessionStore((s) => s.clerkToken)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  const requireClerkReauth = useUIStore((s) => s.requireClerkReauth)
  useEffect(() => {
    if (isAuthenticated && !clerkToken) {
      requireClerkReauth('clerk session missing on POS mount (reload during re-auth)')
    }
  }, [isAuthenticated, clerkToken, requireClerkReauth])

  const isCashInModalOpen = useUIStore((s) => s.isCashInModalOpen)
  const openCashInModal = useUIStore((s) => s.openCashInModal)
  const closeCashInModal = useUIStore((s) => s.closeCashInModal)
  const isCashOutModalOpen = useUIStore((s) => s.isCashOutModalOpen)
  const openCashOutModal = useUIStore((s) => s.openCashOutModal)
  const closeCashOutModal = useUIStore((s) => s.closeCashOutModal)

  // Cash-In gate. Auto-prompt when the cashier ARRIVES needing a shift
  // start, but not as a side effect of a cash-out they just completed
  // in this mounted session.
  //
  // Suppression is driven by an explicit local signal from CashOutModal
  // success (see `onCashOutSuccess` below), NOT inferred from
  // cash-in-status transitions. Transition inference was vulnerable to
  // stale-query replay (`in-shift` cache, then fresh `needs-cash-in`)
  // and could suppress the arrival prompt incorrectly.
  //
  // Defer while the cart has items: a persisted/resumed sale takes
  // precedence. The effect re-evaluates when the cart drains.
  const cartLength = usePOSStore((s) => s.cart.length)
  const { data: cashInStatus } = useGetCashInStatus(true)
  const cashOutCompletedThisMountRef = useRef(false)
  const hasPromptedNeedsCashIn = useRef(false)

  const handleCashOutSuccess = () => {
    cashOutCompletedThisMountRef.current = true
  }

  useEffect(() => {
    if (!cashInStatus) return

    if (cartLength > 0) return
    const needsCashIn = cashInStatus.editable && !cashInStatus.cash_in

    // Re-arm the arrival prompt once status leaves needs-cash-in.
    // Dismissed-without-clock-in does not re-arm because needsCashIn
    // stays true in that case.
    if (!needsCashIn) {
      hasPromptedNeedsCashIn.current = false
      return
    }

    if (cashOutCompletedThisMountRef.current) return
    if (hasPromptedNeedsCashIn.current) return

    openCashInModal()
    hasPromptedNeedsCashIn.current = true
  }, [cashInStatus, cartLength, openCashInModal])

  return (
    <>
      <Outlet />
      <CashInModal
        open={isCashInModalOpen}
        onOpenChange={(open) => (open ? openCashInModal() : closeCashInModal())}
      />
      <CashOutModal
        open={isCashOutModalOpen}
        onOpenChange={(open) => (open ? openCashOutModal() : closeCashOutModal())}
        onCashOutSuccess={handleCashOutSuccess}
      />
      <QuickActionsSheet />
      {/* Non-dismissable clerk re-PIN overlay — raised by the API
          client's clerk-session recovery; sits above every other modal. */}
      <ClerkPinReauthModal />
    </>
  )
}
